import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import html2canvas from 'html2canvas';

const CustomEventPublicTicket = () => {
    const { regId } = useParams();
    const [ticketData, setTicketData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);
    const ticketRef = useRef(null);

    useEffect(() => {
        const fetchTicket = async () => {
            try {
                const isWorkshop = window.location.pathname.includes('/workshop/');
                const endpoint = isWorkshop
                    ? `/api/custom-events/workshop/participant/${regId}`
                    : `/api/custom-events/participant/${regId}`;
                const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`);
                const data = await res.json();

                if (data.success) {
                    // Generate QR on frontend using same format to ensure consistency, 
                    // or we could fetch it, but usually the backend provides QR or we generate it
                    // Wait, we need the exact QR code image. The backend generates it with QRCode.toDataURL.
                    // Instead of regenerating here, let me use the qr code library or the backend can provide it.
                    // Wait, the backend didn't provide it in the GET endpoint. I will generate it here.
                    const QRCode = require('qrcode');
                    const clientUrl = process.env.REACT_APP_CLIENT_URL || window.location.origin;
                    const checkInUrl = `${clientUrl}/event/jss/admin/scan?conf-reg-id=${regId}`;
                    const qrCodeDataUrl = await QRCode.toDataURL(checkInUrl);

                    setTicketData({
                        ...data.data,
                        qrCode: qrCodeDataUrl
                    });
                } else {
                    setError(data.message || 'Ticket not found');
                }
            } catch (err) {
                console.error(err);
                setError('Failed to fetch ticket');
            } finally {
                setLoading(false);
            }
        };

        if (regId) {
            fetchTicket();
        }
    }, [regId]);

    const handleDownload = async () => {
        if (!ticketRef.current || downloading) return;

        try {
            setDownloading(true);
            const canvas = await html2canvas(ticketRef.current, {
                scale: 2, // higher resolution
                useCORS: true,
                backgroundColor: '#ffffff'
            });

            const image = canvas.toDataURL('image/png', 1.0);
            const link = document.createElement('a');
            link.download = `KAPCON_Ticket_${regId}.png`;
            link.href = image;
            link.click();
        } catch (err) {
            console.error('Error downloading ticket', err);
            alert('Failed to download ticket. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5928ed]"></div>
            </div>
        );
    }

    if (error || !ticketData) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl shadow-md max-w-sm w-full text-center">
                    <div className="text-red-500 text-5xl mb-4">⚠️</div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Ticket Not Found</h2>
                    <p className="text-gray-500 text-sm mb-6">{error || 'Invalid Registration ID'}</p>
                    <Link to={window.location.pathname.includes('/workshop/') ? "/event/jss/workshop" : "/event/jss/conference"} className="inline-block bg-[#5928ed] text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-800 transition-colors">
                        Go to Registration
                    </Link>
                </div>
            </div>
        );
    }

    if (ticketData && !ticketData.isApproved) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
                <div className="bg-white p-8 rounded-2xl shadow-md max-w-sm w-full text-center border-t-4 border-[#5928ed]">
                    <div className="text-purple-500 text-5xl mb-4">⏳</div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Registration Under Review</h2>
                    <p className="text-gray-500 text-sm mb-6">We have received your registration details. It is currently pending verification by the organizing committee.</p>
                    <p className="text-gray-500 text-xs mt-4 pb-4 border-b border-gray-100">Once approved, your M-Ticket and QR Check-in Code will appear here. Please check back later or wait for our confirmation email.</p>
                    <Link to={window.location.pathname.includes('/workshop/') ? "/event/jss/workshop" : "/event/jss/conference"} className="inline-block mt-6 text-sm text-[#5928ed] font-medium hover:underline transition-colors">
                        ← Return to Registration
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif" }} className="min-h-screen bg-gradient-to-br from-[#fcfaff] via-[#fef9f5] to-[#f5ede4] py-12 px-4 flex flex-col items-center justify-center">

            <div className="max-w-sm w-full mb-6 text-center">
                <h1 className="text-2xl font-bold text-gray-900 font-sans tracking-tight mb-2">Welcome to KAPCON 2026</h1>
                <p className="text-sm text-gray-500 font-sans mb-4">Here is your official conference M-Ticket</p>
            </div>

            {/* Ticket Container */}
            <div
                ref={ticketRef}
                className="bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden relative"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
                {/* Ticket Header Banner */}
                <div className="bg-[#5928ed] text-center p-6 pb-8 border-b-4 border-purple-900/20">
                    <h2 className="text-white text-3xl font-serif font-bold tracking-tight mb-1 shadow-sm">KAPCON 2026</h2>
                    <p className="text-[#fbd38d] text-[11px] font-bold tracking-wider uppercase opacity-90">52nd Annual State Conference</p>
                </div>

                {/* Ticket Content */}
                <div className="p-6 relative bg-white">
                    {/* Arc cutouts */}
                    <div className="absolute -top-4 -left-4 w-8 h-8 bg-[#fcfaff] rounded-full border border-gray-100 z-10 shadow-inner"></div>
                    <div className="absolute -top-4 -right-4 w-8 h-8 bg-[#fcfaff] rounded-full border border-gray-100 z-10 shadow-inner"></div>
                    <div className="absolute top-0 left-4 right-4 border-t border-dashed border-gray-300 pointer-events-none"></div>

                    <div className="text-center mb-6 pt-2">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-tight">{ticketData.fullName}</h3>
                        <div className="inline-flex items-center px-4 py-1.5 rounded-full text-[11px] font-bold tracking-wide bg-gradient-to-r from-purple-50 to-[#fcfaff] text-[#5928ed] border border-purple-200 shadow-sm uppercase">
                            {ticketData.registrationCategory}
                        </div>
                    </div>

                    <div className="space-y-3 mb-8 bg-[#f8f9fa] p-5 rounded-xl text-sm border border-gray-100 shadow-inner align-left text-left">
                        <div className="flex justify-between items-center pb-3 border-b border-gray-200 border-dashed">
                            <span className="text-gray-500 text-[10px] uppercase font-bold tracking-wider">Reg ID</span>
                            <span className="font-mono font-bold text-gray-900 bg-white px-2 py-0.5 rounded border border-gray-200 shadow-sm text-sm">{ticketData.registrationId}</span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b border-gray-200 border-dashed">
                            <span className="text-gray-500 text-[10px] uppercase font-bold tracking-wider">Designation</span>
                            <span className="font-semibold text-gray-800 truncate max-w-[150px]">{ticketData.designation}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-500 text-[10px] uppercase font-bold tracking-wider">Status</span>
                            <span className={`font-bold tracking-wide ${ticketData.paymentStatus === 'completed' ? 'text-emerald-600' : 'text-rose-500'}`}>
                                {ticketData.paymentStatus.toUpperCase()}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col items-center justify-center mb-6">
                        <div className="p-3 bg-white rounded-xl shadow-md border border-gray-100 relative">
                            <img src={ticketData.qrCode} alt="Ticket QR" className="w-[160px] h-[160px] object-contain" />
                        </div>
                        <p className="text-[10px] font-bold tracking-widest text-[#5928ed] mt-4 uppercase">Scan at Registration Desk</p>
                    </div>

                </div>
                <div className="bg-gradient-to-b from-gray-50 to-gray-100 p-4 text-center border-t border-gray-100">
                    <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">JSS Medical College, Mysuru</p>
                </div>
            </div>

            <div className="mt-8 flex flex-col items-center gap-3 w-full max-w-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="w-full bg-[#111827] hover:bg-black text-white py-3.5 rounded-xl font-bold text-sm transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-75 disabled:cursor-not-allowed"
                >
                    {downloading ? 'Generating PNG...' : (
                        <>
                            <span>⬇</span> Download Ticket as PNG
                        </>
                    )}
                </button>
                <Link to="/event/jss/custom1" className="text-xs text-gray-500 font-medium hover:text-gray-800 transition-colors">
                    Return to Registration View
                </Link>
            </div>

        </div>
    );
};

export default CustomEventPublicTicket;
